import { QueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { checkMultiGuestAvailability } from '../FormSteps/hooks/checkMultiGuestAvailability';
import { PackagesFormValues } from '../types';

interface UseExperienceSameTimesOptions {
  selectedExperiences: PackagesFormValues['selectedExperiences'];
  numberOfGuests: number;
  defaultValue?: boolean;
  queryClient?: QueryClient;
  propertyId?: number;
  spaPackageId?: number;
  spaOpenTime?: string;
  spaCloseTime?: string;
}

interface UseExperienceSameTimesReturn {
  experienceSameTimes: Record<number, boolean>;
  toggleExperienceSameTime: (expIndex: number) => boolean;
  canEnableMultiGuestSameTime: (date: string, optionIds: number[]) => boolean;
}

/**
 * Custom hook to manage the "same time for all guests" state for experiences.
 * Handles initialization from form values and provides a toggle function that
 * updates both local state and form values while clearing guest timeslots.
 */
export const useExperienceSameTimes = ({
  selectedExperiences,
  numberOfGuests,
  defaultValue = false,
  queryClient,
  propertyId,
  spaPackageId,
  spaOpenTime,
  spaCloseTime,
}: UseExperienceSameTimesOptions): UseExperienceSameTimesReturn => {
  const { setValue } = useFormContext<PackagesFormValues>();
  const [experienceSameTimes, setExperienceSameTimes] = useState<Record<number, boolean>>({});
  const experienceSameTimesRef = useRef(experienceSameTimes);
  experienceSameTimesRef.current = experienceSameTimes;

  useEffect(() => {
    if (selectedExperiences) {
      const initialState: Record<number, boolean> = {};
      selectedExperiences.forEach((exp, index) => {
        initialState[index] = exp.guestsUseSameTime ?? defaultValue;
      });
      setExperienceSameTimes(initialState);
    }
  }, [selectedExperiences?.length, defaultValue]);

  const toggleExperienceSameTime = (expIndex: number): boolean => {
    // Read from the ref so rapid consecutive toggles (before re-render) see the
    // latest committed value rather than the closure's stale state.
    const isSameTime = experienceSameTimesRef.current[expIndex] ?? false;
    const newValue = !isSameTime;

    experienceSameTimesRef.current = {
      ...experienceSameTimesRef.current,
      [expIndex]: newValue,
    };
    setExperienceSameTimes(experienceSameTimesRef.current);

    setValue(`selectedExperiences.${expIndex}.guestsUseSameTime`, newValue);

    // Clear all guest timeslots for this experience
    for (let i = 0; i < numberOfGuests; i++) {
      setValue(`guests.${i}.experiences.${expIndex}`, undefined as any);
    }

    return newValue;
  };

  const canEnableMultiGuestSameTime = (date: string, optionIds: number[]): boolean => {
    if (!queryClient || !propertyId || !spaPackageId) {
      return false;
    }

    return optionIds.some((optionId) => {
      if (!optionId) return false;

      return (
        checkMultiGuestAvailability(
          queryClient,
          propertyId,
          spaPackageId,
          date,
          numberOfGuests,
          spaOpenTime || '',
          spaCloseTime || '',
          optionId
        ) ?? false
      );
    });
  };

  return {
    experienceSameTimes,
    toggleExperienceSameTime,
    canEnableMultiGuestSameTime,
  };
};
